import BpmnPropertiesPanel from './BpmnPropertiesPanel';

import {
  isUndo,
  isRedo
} from 'diagram-js/lib/features/keyboard/KeyboardUtil';

import {
  render
} from '@bpmn-io/properties-panel/preact';

import {
  domify,
  query as domQuery,
  event as domEvent
} from 'min-dom';

{ /* Required to break up imports, see https://github.com/babel/babel/issues/15156 */ }

const DEFAULT_PRIORITY = 1000;

/**
 * @typedef { import('@bpmn-io/properties-panel').GroupDefinition } GroupDefinition
 * @typedef { import('@bpmn-io/properties-panel').ListGroupDefinition } ListGroupDefinition
 * @typedef { GroupDefinition | ListGroupDefinition } Group
 * @typedef { {
 *   getGroups: (element: djs.model.Base) => (groups: Group[]) => Group[],
 *   getEntryId?: (element: djs.model.Base, path: (string|number)[]) => string|null
 * } } PropertiesProvider
 */

export default class BpmnPropertiesPanelRenderer {

  constructor(config, injector, eventBus) {
    const {
      parent,
      layout: layoutConfig,
      description: descriptionConfig,
      tooltip: tooltipConfig,
      feelPopupContainer,
      getFeelPopupLinks,
      feelLanguageContext
    } = config || {};

    this._eventBus = eventBus;
    this._injector = injector;
    this._layoutConfig = layoutConfig;
    this._descriptionConfig = descriptionConfig;
    this._tooltipConfig = tooltipConfig;
    this._feelPopupContainer = feelPopupContainer;
    this._getFeelPopupLinks = getFeelPopupLinks;
    this._feelLanguageContext = feelLanguageContext;

    this._container = domify(
      '<div style="height: 100%" tabindex="-1" class="bio-properties-panel-container"></div>'
    );

    this._headerContainer = domify(
      '<div style="flex: none; height: auto" class="bio-properties-panel bio-properties-panel-header-container"></div>'
    );

    this._separateHeader = false;

    var commandStack = injector.get('commandStack', false);

    commandStack && setupKeyboard(this._container, eventBus, commandStack);

    eventBus.on('diagram.init', () => {
      if (parent) {
        this.attachTo(parent);
      }
    });

    eventBus.on('diagram.destroy', () => {
      this.detach();
    });

    eventBus.on('root.added', (event) => {
      const { element } = event;

      this._render(element);
    });
  }


  /**
   * Attach the properties panel to a parent node.
   *
   * Pass a second container to render the header separately from the body.
   *
   * @param {HTMLElement} container body container
   * @param {HTMLElement} [headerContainer] optional separate header container
   */
  attachTo(container, headerContainer) {
    if (!container) {
      throw new Error('container required');
    }

    container = resolveContainer(container);

    if (headerContainer) {
      headerContainer = resolveContainer(headerContainer);

      if (!headerContainer) {
        throw new Error('header container not found');
      }
    }

    const separateHeader = !!headerContainer;

    const headerPlacementChanged = separateHeader !== this._separateHeader;

    // (1) detach from old parent
    this.detach();

    // (2) append body to its container
    container.appendChild(this._container);

    // (3) append header to its container or render it inline
    this._separateHeader = separateHeader;

    if (headerContainer) {
      headerContainer.appendChild(this._headerContainer);
    }

    // (4) re-render if the header placement changed
    if (headerPlacementChanged) {
      this._rerender();
    }

    // (5) notify interested parties
    this._eventBus.fire('propertiesPanel.attach');
  }

  /**
   * Detach the properties panel from its parent node.
   */
  detach() {
    const parentNode = this._container.parentNode;

    if (parentNode) {
      parentNode.removeChild(this._container);

      this._eventBus.fire('propertiesPanel.detach');
    }

    const headerParentNode = this._headerContainer.parentNode;

    if (headerParentNode) {
      headerParentNode.removeChild(this._headerContainer);
    }
  }

  /**
   * Register a new properties provider to the properties panel.
   *
   * @param {Number} [priority]
   * @param {PropertiesProvider} provider
   */
  registerProvider(priority, provider) {

    if (!provider) {
      provider = priority;
      priority = DEFAULT_PRIORITY;
    }

    if (typeof provider.getGroups !== 'function') {
      console.error(
        'Properties provider does not implement #getGroups(element) API'
      );

      return;
    }

    this._eventBus.on('propertiesPanel.getProviders', priority, function(event) {
      event.providers.push(provider);
    });

    this._eventBus.fire('propertiesPanel.providersChanged');
  }

  /**
   * Updates the layout of the properties panel.
   * @param {Object} layout
   */
  setLayout(layout) {
    this._eventBus.fire('propertiesPanel.setLayout', { layout });
  }

  /**
   * Set the FEEL language context used by properties panel entries.
   *
   * @param {Object} feelLanguageContext
   */
  setFeelLanguageContext(feelLanguageContext) {
    this._feelLanguageContext = feelLanguageContext;

    this._rerender();
  }

  /**
   * Resolve the id of the entry that edits the given moddle property path,
   * asking registered providers in reverse render order (the provider whose
   * groups render last answers first). Providers not implementing
   * #getEntryId(element, path), or returning a falsy value, are skipped.
   *
   * @param {djs.model.Base} element
   * @param {(string|number)[]} path moddle property path, relative to the element's business object
   *
   * @return {string|null}
   */
  getEntryId(element, path) {
    const providers = this._getProviders().slice().reverse();

    for (const provider of providers) {
      if (typeof provider.getEntryId !== 'function') {
        continue;
      }

      const entryId = provider.getEntryId(element, path);

      if (entryId) {
        return entryId;
      }
    }

    return null;
  }

  _getProviders() {
    const event = this._eventBus.createEvent({
      type: 'propertiesPanel.getProviders',
      providers: []
    });

    this._eventBus.fire(event);

    return event.providers;
  }

  _render(element) {
    const canvas = this._injector.get('canvas');

    if (!element) {
      element = canvas.getRootElement();
    }

    if (isImplicitRoot(element)) {
      return;
    }

    render(
      <BpmnPropertiesPanel
        element={ element }
        injector={ this._injector }
        getProviders={ this._getProviders.bind(this) }
        layoutConfig={ this._layoutConfig }
        descriptionConfig={ this._descriptionConfig }
        tooltipConfig={ this._tooltipConfig }
        feelPopupContainer={ this._feelPopupContainer }
        getFeelPopupLinks={ this._getFeelPopupLinks }
        feelLanguageContext={ this._feelLanguageContext }
        headerParent={ this._separateHeader ? this._headerContainer : null }
      />,
      this._container
    );

    this._eventBus.fire('propertiesPanel.rendered');
  }

  _rerender() {
    const canvas = this._injector.get('canvas');

    const rootElement = canvas.getRootElement();

    if (rootElement && !isImplicitRoot(rootElement)) {
      this._render(rootElement);
    }
  }

  _destroy() {
    if (this._container) {
      render(null, this._container);

      this._eventBus.fire('propertiesPanel.destroyed');
    }
  }
}

BpmnPropertiesPanelRenderer.$inject = [ 'config.propertiesPanel', 'injector', 'eventBus' ];


// helpers ///////////////////////

function isImplicitRoot(element) {
  return element && element.isImplicit;
}

/**
 * Resolve a container that may be given as a jQuery object, a selector or a
 * plain HTML element into a plain HTML element.
 *
 * @param {HTMLElement|string|Object} container
 *
 * @return {HTMLElement}
 */
function resolveContainer(container) {

  // unwrap jQuery if provided
  if (container.get && container.constructor.prototype.jquery) {
    return container.get(0);
  }

  if (typeof container === 'string') {
    return domQuery(container);
  }

  return container;
}

/**
 * Setup keyboard bindings (undo, redo) on the given container.
 *
 * @param {Element} container
 * @param {EventBus} eventBus
 * @param {CommandStack} commandStack
 */
function setupKeyboard(container, eventBus, commandStack) {

  function cancel(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleKeys(event) {

    if (isUndo(event)) {
      commandStack.undo();

      return cancel(event);
    }

    if (isRedo(event)) {
      commandStack.redo();

      return cancel(event);
    }
  }

  eventBus.on('keyboard.bind', function() {
    domEvent.bind(container, 'keydown', handleKeys);
  });

  eventBus.on('keyboard.unbind', function() {
    domEvent.unbind(container, 'keydown', handleKeys);
  });
}